import React from "react";
import { ChevronDown, Clock, DollarSign } from "lucide-react";

import { summariseTurnLog } from "./utils/turnCost";

/**
 * The header's "N turns · Xs · $Y" button and its Turn breakdown popover.
 *
 * Every number shown — header count, totals, Avg line, each turn's total and
 * each row's cost — is derived from the same turn log by summariseTurnLog, so
 * a turn's rows add up to its total and the turns add up to the session total
 * at the displayed precision. A turn's unitemised cost is its own "other" row.
 */
export default function TurnBreakdownPanel({ turnLog, fallbackTurns = 0, open, onToggle, wrapRef }) {
  const summary = summariseTurnLog(turnLog);
  const count = summary.count || fallbackTurns;
  if (count <= 0) return null;
  return (
    <div className="d5-stats-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`d5-copilot-stats${open ? " d5-copilot-stats--active" : ""}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {count} turn{count !== 1 ? "s" : ""}
        {summary.totalLatencyMs > 0 && ` · ${(summary.totalLatencyMs / 1000).toFixed(1)}s`}
        {summary.totalCostUsd > 0 && ` · ${summary.totalCostText}`}
        <ChevronDown size={10} className={`d5-stats-chevron${open ? " d5-stats-chevron--open" : ""}`} />
      </button>
      <div className={`d5-turn-panel${open ? " d5-turn-panel--open" : ""}`}>
        <div className="d5-turn-panel-head">
          <span>Turn breakdown</span>
          <span className="d5-turn-panel-total">{count} total</span>
        </div>
        <div className="d5-turn-col-labels">
          <span />
          <span>Turn</span>
          <span>Share</span>
          <span className="d5-turn-col-r">Time</span>
          <span className="d5-turn-col-r">Cost</span>
        </div>
        <div className="d5-turn-rows">
          {summary.rows.map((t) => {
            const share = summary.totalLatencyMs > 0 ? (t.latencyMs / summary.totalLatencyMs) * 100 : 0;
            // No stored timing: an EMPTY track, not a floor-width bar that would read as "took ~no time".
            const hasTiming = t.latencyMs > 0;
            const hasSteps = t.steps?.length > 0;
            const turnRowEl = (
              <div className="d5-turn-row" data-testid={`turn-row-${t.n}`}>
                <span className="d5-turn-caret">{hasSteps ? "▶" : ""}</span>
                <span className="d5-turn-n">#{t.n}{t.cacheHit ? " ⚡" : ""}</span>
                <span className={`d5-turn-bar-track${hasTiming ? "" : " d5-turn-bar-track-empty"}`}>
                  {hasTiming && <span className="d5-turn-bar" style={{ width: `${Math.max(share, 2)}%` }} />}
                </span>
                <span className="d5-turn-lat">{t.latencyMs > 0 ? `${(t.latencyMs / 1000).toFixed(1)}s` : "—"}</span>
                <span className="d5-turn-cost">{t.costText}</span>
              </div>
            );
            if (!hasSteps) return <div key={t.n}>{turnRowEl}</div>;
            return (
              <details key={t.n} className="d5-turn-details">
                <summary className="d5-turn-summary">{turnRowEl}</summary>
                <div className="d5-breakdown-body">
                  {t.steps.map((s, si) => {
                    const kind = s.kind || "step";
                    const kindLabel = kind === "llm" ? "model" : kind;
                    const ms = Number(s.ms);
                    const msLabel = !Number.isFinite(ms) ? "" : ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${Math.round(ms)}ms`;
                    const extra = [s.out != null && `${s.out}tok`, s.costText].filter(Boolean).join(" ");
                    return (
                      <div className="d5-breakdown-row" key={si} data-testid="breakdown-row">
                        <span className={`d5-breakdown-kind d5-breakdown-kind-${kind}`}>{kindLabel}</span>
                        <span className="d5-breakdown-label" title={s.label}>{s.label}</span>
                        <span className="d5-breakdown-ms">{msLabel}</span>
                        {extra && <span className="d5-breakdown-extra">{extra}</span>}
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })}
        </div>
        <div className="d5-turn-panel-foot">
          {/* Gated on the totals: a session stored before per-turn metrics replays with zeros. */}
          {summary.totalLatencyMs > 0 && (
            <span className="d5-turn-foot-stat">
              <Clock size={10} />
              Avg {summary.avgLatencyS}s/turn
            </span>
          )}
          {summary.totalCostUsd > 0 && (
            <span className="d5-turn-foot-stat d5-turn-foot-stat-cost">
              <DollarSign size={10} />
              {summary.avgCostText.slice(1)}/turn
            </span>
          )}
          {summary.totalLatencyMs <= 0 && summary.totalCostUsd <= 0 && (
            <span className="d5-turn-foot-stat">Timings not recorded for this session</span>
          )}
        </div>
      </div>
    </div>
  );
}
